import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { avvColumns, avvSorting, serviceColumns, storageLocationColumns } from '@Constants/TableColumns';
import type { ApiAvv, ApiService, ApiStorageLocation, ApiStorageTransaction } from '@Model';
import { EnumFrontendLayoutSetting, EnumStorageTransactionType } from '@Model';
import {
    type AsyncStateUpdate,
    DateInput,
    Grid,
    GridColumn,
    GridColumns,
    type IGuiBinder,
    NumberInput,
    type SelectOption,
    SingleSelect,
    TextInput,
    useAutoConfirmed,
    useProp,
    useTranslate,
} from '@synqo/frontend-component-library';
import { memo } from 'react';
import { AvvSuggestions } from '../AvvSuggestions';
import { type BookingErrors } from '../bookingRules';
import { useStorageTransactionLinks } from '../useStorageTransactionLinks';
import classes from './StorageTransactionBookingSection.module.css';

interface IProps {
    binder: IGuiBinder<ApiStorageTransaction>;
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiStorageTransaction>;

    transactionType: EnumStorageTransactionType;
    quantityEntered: boolean;
    onQuantityTyped: () => void;
    onQuantityConfirmed: () => void;
    errors: BookingErrors;
    focusMaterial: boolean;
    storageLocationId: string | undefined;
    materialId: string | undefined;
    avvId: string | undefined;
    occurredAt: string;
}

const typeOptions: SelectOption<EnumStorageTransactionType>[] = [
    { name: 'incoming', value: EnumStorageTransactionType.Incoming, label: '+ Zugang' },
    { name: 'outgoing', value: EnumStorageTransactionType.Outgoing, label: '− Abgang' },
    { name: 'stocktaking', value: EnumStorageTransactionType.Stocktaking, label: '= Inventur' },
];

// @ai-generated 2026-10-23T16:10:00+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export const StorageTransactionBookingSection = memo(function StorageTransactionBookingSection({
    binder,
    baseApiObjectPath,
    requestUpdateState,
    transactionType,
    quantityEntered,
    onQuantityTyped,
    onQuantityConfirmed,
    errors,
    focusMaterial,
    storageLocationId,
    materialId,
    avvId,
    occurredAt,
}: IProps) {
    const i18n = useTranslate('');
    const isStocktaking = transactionType === EnumStorageTransactionType.Stocktaking;

    const links = useStorageTransactionLinks(binder, baseApiObjectPath, requestUpdateState);

    const typeField = useAutoConfirmed<ApiStorageTransaction, EnumStorageTransactionType>(binder, 'type');
    const occurredAtField = useProp(binder, 'occurred_at');
    const quantityField = useProp<ApiStorageTransaction, number>(binder, 'quantity');
    const remarksField = useProp(binder, 'remarks');
    // React keeps a typed "0" in a number input, so the field remounts to show the reformatted "0,000"
    const weightKey = isStocktaking && quantityEntered ? 'entered' : 'blank';

    return (
        <Grid columns={12}>
            <GridColumn span={12}>
                <SingleSelect<EnumStorageTransactionType>
                    {...typeField}
                    items={typeOptions}
                    label={i18n('Buchungsart')}
                    required
                    fullWidth
                />
            </GridColumn>
            <GridColumn span={12}>
                <LinkedFieldWithLayoutSettings<ApiStorageLocation>
                    layoutSettingsContext={EnumFrontendLayoutSetting.StorageLocationsTableLayout}
                    {...links.storageLocation}
                    columns={storageLocationColumns}
                    label={i18n('Lagerort')}
                    required
                />
            </GridColumn>
            <GridColumns spans={[6, 6]}>
                <LinkedFieldWithLayoutSettings<ApiService>
                    layoutSettingsContext={EnumFrontendLayoutSetting.ServicesTableLayout}
                    {...links.material}
                    columns={serviceColumns}
                    label={i18n('Material')}
                    required
                    autoFocusOnRender={focusMaterial}
                />
                <LinkedFieldWithLayoutSettings<ApiAvv>
                    layoutSettingsContext={EnumFrontendLayoutSetting.AvvsTableLayout}
                    {...links.avv}
                    columns={avvColumns}
                    sorting={avvSorting}
                    label={i18n('AVV')}
                />
            </GridColumns>
            <GridColumn span={12}>
                <AvvSuggestions
                    storageLocationId={storageLocationId}
                    materialId={materialId}
                    avvId={avvId}
                    occurredAt={occurredAt}
                    onSelect={(id) => void links.linkAvv(id)}
                />
            </GridColumn>
            <GridColumns spans={[6, 6]}>
                <DateInput
                    {...occurredAtField}
                    type="datetime"
                    maxDate={new Date()}
                    label={i18n('Zeitpunkt')}
                    required
                    validator={() => errors.occurredAt ?? true}
                />
                <NumberInput
                    {...quantityField}
                    key={weightKey}
                    className={classes.weight}
                    decimals={3}
                    label={i18n(isStocktaking ? 'Bestand (t)' : 'Gewicht (t)')}
                    required
                    blankWhenZero={weightKey === 'blank'}
                    onChange={(value, valid) => {
                        onQuantityTyped();
                        return quantityField.onChange(value, valid);
                    }}
                    onConfirm={async (value, valid) => {
                        const confirmed = await quantityField.onConfirm?.(value, valid);
                        if (confirmed) {
                            onQuantityConfirmed();
                        }
                        return confirmed ?? false;
                    }}
                    validator={(value) => errors.quantity ?? quantityField.validator?.(value) ?? true}
                />
            </GridColumns>
            <GridColumn span={12}>
                <TextInput {...remarksField} label={i18n('Bemerkung')} />
            </GridColumn>
        </Grid>
    );
});
